import { mdiPail, mdiPlus } from '@mdi/js'
import Icon from '@mdi/react'
import { FetchLists, FetchPostDestinations } from 'components/Fetch'
import { FetchBuckets } from 'components/Fetch/FetchBuckets'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import { SelectableAvatar } from 'components/ProfileAvatar'
import { Fragment, memo, useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import BucketCreatePopup from 'routes/publishing/routes/buckets/components/BucketCreatePopup'
import { setComposerBucket } from 'services/compose'
import { activePostIdSelector, composerSelectedBucketIdSelector, composerSelectedProfilesIdsSelector } from 'services/compose/selectors'
import { sortedConnectedProfilesSelector } from 'services/destinations'
import { bucketsSelector } from 'services/post/selectors'
import { setComposerProfiles } from 'services/shared/actions'
import { Bucket } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import Accordion from '../Accordion'
import ProfilesDropdown from '../ProfilesDropdown'
import { ConnectedProfilesWarningPopup } from './ConnectedProfilesWarningPopup'
import styles from './ProfileSelection.pcss'
import { PostDestination } from 'shared'

export function ProfileSelection() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const connectedProfiles: PostDestination[] = useSelector(sortedConnectedProfilesSelector)
  const selectedProfilesIds: string[] = useSelector(composerSelectedProfilesIdsSelector)
  const selectedBucketId: string | undefined = useSelector(composerSelectedBucketIdSelector)
  const buckets: Bucket[] = useSelector(bucketsSelector)
  const bucketProfiles = selectedBucketId ? buckets.find(b => b.id === selectedBucketId)?.ppids || null : null
  const [selectedBucketProfiles, setSelectedBucketProfiles] = useState<{ ppids: string[], selectedPpids: string[] } | null>(
    bucketProfiles ? { ppids: bucketProfiles, selectedPpids: bucketProfiles } : null
  )
  const isEditMode = Boolean(useSelector(activePostIdSelector))
  const [loading, setLoading] = useState(true)
  const [showCreateBucket, setShowCreateBucket] = useState(false)

  const visibleBuckets = useMemo(() => buckets.filter(b => b.ppids.length > 0).map((b, i) => ({ ...b, order: i })), [buckets])
  const visibleProfiles = useMemo(() => {
    if (selectedBucketProfiles) {
      return connectedProfiles.filter(p => selectedBucketProfiles.ppids.includes(p.ppid))
    }
    return connectedProfiles.filter(p => selectedProfilesIds.includes(p.id))
  }, [connectedProfiles, selectedBucketProfiles, selectedProfilesIds])

  // EXPL: the grid item handlers below are passed to a memoized child, so their identity has to
  // survive a selection change - reading the mutable bits from refs keeps the deps list at
  // `[dispatch]`. Held as deps they changed on exactly the click the memo is meant to cheapen,
  // and every ProfileGridItem re-rendered anyway.
  const visibleProfilesRef = useRef(visibleProfiles)
  visibleProfilesRef.current = visibleProfiles
  const selectedBucketProfilesRef = useRef(selectedBucketProfiles)
  selectedBucketProfilesRef.current = selectedBucketProfiles

  const bucketSelectOptions: PPSelectOptions = useMemo(() => {
    const bucketOptions = visibleBuckets.reduce((options: PPSelectOptions, bucket) => {
      options[bucket.id] = {
        label: (
          <span className={styles['bucket-name']}>
            <Icon path={mdiPail} className={styles['icon-bucket']} style={{ color: bucket.color }} />
            <div className="text-ellipsis">{bucket.name}</div>
          </span>
        ),
        order: bucket.order
      }
      return options
    }, {})

    bucketOptions.none = {
      label: (
        <span className={styles['bucket-name']}>
          <FormattedMessage id="label.generic.none" />
        </span>
      ),
      order: -1
    }

    bucketOptions.create = {
      label: (
        <span className={styles['bucket-name']}>
          <Icon path={mdiPlus} className={styles['icon-bucket']} />
          <div className="text-ellipsis">
            <FormattedMessage id="actions.add-new" />
          </div>
        </span>
      ),
      order: 999
    }

    return bucketOptions
  }, [visibleBuckets])

  const onBucketSelected = useCallback((b: string | Bucket) => {
    const id = typeof b === 'string' ? b : b.id
    if (id === 'create') {
      setShowCreateBucket(true)
      return
    }
    if (id === 'none') {
      dispatch(setComposerBucket(undefined))
      setSelectedBucketProfiles(null)
      return
    }
    const bucket = typeof b === 'string' ? buckets.find(b => b.id === id) : b as Bucket
    if (bucket) {
      const profiles = connectedProfiles.filter(p => bucket.ppids.includes(p.ppid))
      setSelectedBucketProfiles({ ppids: bucket.ppids, selectedPpids: bucket.ppids })
      dispatch(setComposerBucket(bucket.id))
      dispatch(setComposerProfiles(profiles))
    }
  }, [buckets, connectedProfiles, dispatch])

  const onBucketCreated = (b: Bucket) => {
    setShowCreateBucket(false)
    // Wait for the bucket to be added to the store before selecting it
    setTimeout(() => {
      onBucketSelected(b)
    }, 250) // eslint-disable-line no-magic-numbers
  }

  const closeBucketCreatePopup = () => {
    setShowCreateBucket(false)
  }

  const stopLoading = useCallback(() => {
    setLoading(false)
  }, [])

  const onProfileClick = useCallback((profile: PostDestination) => {
    const bucketSelection = selectedBucketProfilesRef.current
    const profiles = visibleProfilesRef.current
    let nextSelection: PostDestination[] = []
    if (bucketSelection) {
      let selectedPpids = bucketSelection.selectedPpids.includes(profile.ppid)
        ? bucketSelection.selectedPpids.filter(p => p !== profile.ppid)
        : [...bucketSelection.selectedPpids, profile.ppid]

      if (selectedPpids.length === 0) {
        selectedPpids = bucketSelection.selectedPpids
      }
      nextSelection = profiles.filter(p => selectedPpids.includes(p.ppid))

      setSelectedBucketProfiles({
        ppids: bucketSelection.ppids,
        selectedPpids
      })
    } else {
      nextSelection = profiles.filter(p => p.id !== profile.id)
    }
    dispatch(setComposerProfiles(nextSelection))
  }, [dispatch])

  const onProfileDblClick = useCallback((profile: PostDestination) => {
    if (selectedBucketProfilesRef.current) {
      setSelectedBucketProfiles(current => {
        if (!current) {
          return null
        }
        return { ...current, selectedPpids: [profile.ppid] }
      })
    }
    dispatch(setComposerProfiles([profile]))
  }, [dispatch])

  const onDropdownProfilesSelectionChange = useCallback(() => {
    setSelectedBucketProfiles(null)
  }, [])

  return (
    <Accordion
      title={(
        <FormattedMessage id="composer.titles.profile-selection" />
      )}
      id="composer-profile-selection"
      order="1"
      headerElements={(
        <Fragment>
          {visibleBuckets.length > 0 && !isEditMode && (
            <PPSelect
              name={(
                <span className={styles['btn-label']}><FormattedMessage id="label.generic.bucket" /></span>
              )}
              options={bucketSelectOptions}
              selectedValue={selectedBucketId || 'none'}
              withCaret
              raised
              listClassName={styles['bucket-dd-list']}
              onSelectionChange={onBucketSelected}
            />
          )}
          <ProfilesDropdown btnClassName={styles.dropdown} onSelectionChange={onDropdownProfilesSelectionChange} />
        </Fragment>
      )}
    >
      <div className={styles.grid} data-testid="composer-selected-profiles-list">
        {visibleProfiles.map(profile => (
          <ProfileGridItem
            profile={profile}
            key={profile.id}
            selected={!selectedBucketId || Boolean(selectedBucketProfiles?.selectedPpids.includes(profile.ppid))}
            onClick={onProfileClick}
            onDoubleClick={onProfileDblClick}
          />
        ))}
      </div>
      <FetchPostDestinations force={false} onFetched={stopLoading} onFailed={stopLoading} />
      <FetchBuckets />
      <FetchLists force={false} />
      <ConnectedProfilesWarningPopup open={!loading && connectedProfiles.length === 0} />
      <BucketCreatePopup
        open={showCreateBucket}
        onCreated={onBucketCreated}
        onClose={closeBucketCreatePopup}
      />
    </Accordion>
  )
}

const ProfileGridItem = memo(function ProfileGridItem({ profile, selected, onClick, onDoubleClick }: {
  profile: PostDestination,
  selected: boolean,
  onClick: (p: PostDestination) => void,
  onDoubleClick: (p: PostDestination) => void
}) {
  const cancelClick = useRef(false)

  const onProfileClick = (e: any) => {
    if (e.detail > 1) {
      cancelClick.current = true
      onDoubleClick(profile)
    } else {
      setTimeout(() => {
        if (!cancelClick.current) {
          onClick(profile)
        }
        cancelClick.current = false
      }, 300) // eslint-disable-line no-magic-numbers
    }
  }

  return (
    <div className={styles['grid-item']} data-id={profile.id} onClick={onProfileClick}>
      <SelectableAvatar
        circle={!profile.fbGroup}
        src={profile.image}
        brand={profile.type}
        title={profile.name}
        selected={selected}
      />
    </div>
  )
})

export default ProfileSelection
